import { has, isEmpty } from 'lodash';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { Fragment, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import URI from 'urijs';
import { constants, messageFormat } from '@clubmed/utils';
import { getInstallmentPrice } from 'Components/utils/price/price.util';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { BookingCalendarMonths } from 'Components/widgets/booking-calendar-months/BookingCalendarMonths';
import { BookingCalendarResume } from 'Components/widgets/booking-calendar-resume/BookingCalendarResume';
import { ArrivalDepartureDate } from 'Components/widgets/criteria/criteria-when/filter-when/ArrivalDepartureDate';
import { AsyncLoader } from 'Components/widgets/loading/async-loader/AsyncLoader';
import { NAVIGATION_OFFSET_SELECTOR } from 'Components/widgets/navigation/Navigation.constants';
import { useAvailableDurations } from 'Components/widgets/price-remote/price-remote-form/useAvailableDurations';
import { TripDatePicker } from 'Components/widgets/trip-date-picker/TripDatePicker';
import { dateDiff } from 'Components/widgets/trip-date-picker/TripDatePicker.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { useBlockInputDepartureDate } from 'Src/hooks/use-block-input-departure-date/useBlockInputDepartureDate';
import { usePriceOfStay } from 'Src/hooks/use-price-of-stay/usePriceOfStay';
import { t } from 'Src/i18n';
import {
  clearCalendar,
  fetchCalendar,
  fetchCalendarTripPrice,
  selectMonth,
  setInitialDuration,
  setIsPriceCalendarSelection,
  setPossibleDepartureDate,
} from 'Store/calendar/calendar.action';
import type { CalendarData } from 'Store/calendar/calendar.helper';
import 'Store/calendar/calendar.helper';
import {
  getCalendar,
  getCalendarLoading,
  getCalendarTripPrice,
  getCalendarTripPriceLoading,
  getDays,
  getPricesByDays,
  getSelectedMonth,
} from 'Store/calendar/calendar.selector';
import { changeTripDuration, clearTripDuration } from 'Store/faceting/facetingSearch.action';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { getTripDuration } from 'Store/trip-criteria/tripCriteria.selector';
import { getDepartureDate } from 'Store/trip-date-criteria/storeHelper';
import { updateTripDateCriteria } from 'Store/trip-date-criteria/tripDateCriteria.action';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import {
  QUERY_DATE_FORMAT,
  QUERY_DEPARTURE_DATE,
  QUERY_DURATION,
  QUERY_TRANSPORT,
} from 'Utils/uri/constants';
import { PriceLegend } from './PriceLegend';
const { NO_TRANSPORT_VALUE } = constants;
const HASH = 'PriceCalendarResume';
const SCROLL_ANCHOR = 'PriceCalendar';
type Props = {
  bookingConfig: {
    enableTransfer: boolean | null | undefined;
    form: {
      title: string | null | undefined;
      dateFormat: string | null | undefined;
      dateValidate: string | null | undefined;
      selectDuration: string | null | undefined;
      durationTitle: string | null | undefined;
      legalNotice: string;
      selectChildrenLabels: any | null | undefined;
      selectAdultsLabels: any | null | undefined;
      errorMaxGuestsLabel: string | null | undefined;
    };
    priceCalendar: {
      offers: {
        urgentisation: {
          label: string;
          rateToDisplay: number;
        };
      };
    };
  };
  didMountCallback: (arg0: any) => void;
  duration: number;
  handleChange?: any;
  isJoyview: boolean;
  offsetSell: number;
  onSelectDate: (arg0: any) => void;
  product: Product;
  productId: string;
  transport: string;
};
const YEAR_MONTH = 'YYYYMM';

const defaultHandleChange = (cb) => () => cb();

export const PriceCalendar = (props: Props) => {
  const appContext = useAppContext();
  const router = useRouter();
  const uri = new URI(router.asPath);
  const uriRef = React.useRef(null);
  uriRef.current = uri;
  const { query: queryParams } = router;
  const {
    bookingConfig,
    didMountCallback,
    duration,
    handleChange = defaultHandleChange,
    isJoyview,
    offsetSell,
    product,
    productId,
    transport,
  } = props;
  const urgentisationRate = bookingConfig.priceCalendar.offers.urgentisation.rateToDisplay;
  const dispatch = useDispatch();
  const calendar = useSelector<CalendarData>(getCalendar);
  const days = useSelector<Array<any>>(getDays);
  const selectedMonth = useSelector<number>(getSelectedMonth);
  const selectedDay = useSelector<number>(getDepartureDate);
  const isLoading = useSelector<boolean>(getCalendarLoading);
  const prices = useSelector(getPricesByDays);
  const departureDate = useSelector((state) => state.tripCriteria.departureDate);
  const tripDuration = useSelector(getTripDuration);
  const { blockInputDepartureDate, setBlockInputDepartureDate } = useBlockInputDepartureDate(true);
  const [durationPrice] = useState(duration);
  const price = useSelector<ProductPrice>(getCalendarTripPrice);
  const priceIsLoading = useSelector<boolean>(getCalendarTripPriceLoading);
  const { isDesktop, isTablet } = useResponsive();
  const track = useTracking({
    actionGA: 'calendarPrice',
  });
  const [hasToaster, setHasToaster] = useState(false);
  const [inputDuration, setInputDuration] = useState(tripDuration);
  const {
    hasValidCriteria,
    isLoading: isPriceOfStayLoading,
    priceOfStay,
  } = usePriceOfStay(productId, transport);
  useEffect(() => {
    if (inputDuration && inputDuration !== durationPrice) {
      dispatch(
        fetchCalendarTripPrice({
          appContext,
          duration: inputDuration,
          forceAdultPerTrip: true,
          location: {
            // this done to avoid regression, to do clean this when every thing is migrated
            queryParams,
            urlParams: queryParams,
          },
          productId,
          transport,
        }),
      );
    }
  }, [productId, inputDuration, transport]);
  const [openCalendarToDate, setCalendarToDate] = useState(
    !blockInputDepartureDate && departureDate
      ? moment(departureDate)
      : moment(selectedMonth, YEAR_MONTH),
  );
  useEffect(() => {
    if (tripDuration !== inputDuration) {
      setInputDuration(tripDuration);
    }
  }, [tripDuration]);
  useEffect(() => {
    if (!departureDate) {
      setCalendarToDate(moment(selectedMonth, YEAR_MONTH));
    }
  }, [selectedMonth]);
  const availableDurations = useAvailableDurations({
    productId,
    initialTransport: transport,
  });

  const setUrlQueries = (queries: Record<string, string | number>) => {
    if (uriRef.current) {
      const searchPageUrl = uriRef.current.clone().setQuery(queries);
      router.replace(searchPageUrl.toString(), undefined, {
        shallow: true,
        scroll: false,
      });
      scrollToElement(
        `[data-scroll-anchor="${SCROLL_ANCHOR}"]`,
        500,
        `.${NAVIGATION_OFFSET_SELECTOR}`,
      );
    }
  };

  const onSelect = (dates: { from: Date | null | undefined; to: Date | null | undefined }) => {
    const timestamp = dates.from && moment(dates.from).valueOf();

    if (blockInputDepartureDate) {
      setBlockInputDepartureDate(false);
    }

    if (departureDate !== timestamp) {
      handleChange(() => {
        batch(() => {
          dispatch(setDepartureDate(timestamp));
          dispatch(clearTripDuration());
        });
        setInputDuration(null);
        requestAnimationFrame(() => {
          setUrlQueries({
            [QUERY_DEPARTURE_DATE]:
              (timestamp || undefined) && moment(timestamp).format(QUERY_DATE_FORMAT),
            [QUERY_DURATION]: undefined,
          });
        });

        if (timestamp) {
          track({
            labelGA: `when_${moment(timestamp).format('DD/MM/YYYY')}`,
          });
          setCalendarToDate(moment(timestamp));
        }
      })();
    }

    if (dates.from && dates.to) {
      const updatedDuration = dateDiff(dates.from, dates.to);
      track({
        labelGA: `duration_${updatedDuration}`,
      });
      setInputDuration(updatedDuration);
      batch(() => {
        dispatch(changeTripDuration(updatedDuration));
        const dayId = parseInt(moment(dates.from).format(API_DATE_FORMAT), 10);
        const day = days.find((calendarDay) => {
          return calendarDay.dayContent.id === dayId;
        });
        day &&
          dispatch(
            updateTripDateCriteria({
              day: day.dayContent,
            }),
          );
        dispatch(setIsPriceCalendarSelection(true));
      });
      setHasToaster(true);
      setUrlQueries({
        [QUERY_DURATION]: updatedDuration,
      });
    } else {
      setInputDuration(null);
      dispatch(updateTripDateCriteria(null));
      setHasToaster(false);
    }
  };

  useEffect(() => {
    dispatch(setInitialDuration(duration));
    return () => {
      dispatch(clearCalendar());
    };
  }, []);
  const transportQueryValue = queryParams[QUERY_TRANSPORT];
  useEffect(() => {
    const hasProposalIdQueryParam = has(queryParams, 'proposalId');

    if (
      hasProposalIdQueryParam ||
      !productId ||
      (transportQueryValue && transportQueryValue !== transport)
    ) {
      return; // because a Redux action will fetch the calendar
    }

    if (selectedMonth) {
      setCalendarToDate(moment(selectedMonth, YEAR_MONTH));
    }

    dispatch(
      fetchCalendar(
        {
          resortId: productId,
          transport,
          duration: durationPrice,
          urgentisationRate,
        },
        appContext,
      ),
    );

    if (hasToaster) {
      setHasToaster(false);
    }
  }, [productId, transport, transportQueryValue, urgentisationRate]);
  useEffect(() => {
    dispatch(setPossibleDepartureDate());

    /**
     * Should fetch proposals after state update for arrivaldate
     * see BookingEngineForm, ligne 170.
     * */
    didMountCallback && didMountCallback();
  }, [calendar.months]);

  const onSelectDate = (day) => {
    props.onSelectDate(day);
  };

  /**
   * make sure the selected date is up to date after a transport update
   */
  useEffect(() => {
    if (selectedDay) {
      const day = days.find((calendarDay) => {
        return calendarDay.dayContent.id === selectedDay;
      });
      day && onSelectDate(day.dayContent);
    }
  }, [JSON.stringify(calendar), transport, selectedDay]);
  useEffect(() => {
    if (hasToaster) {
      requestAnimationFrame(() => {
        scrollToElement(`#${HASH}`, 500, `.${NAVIGATION_OFFSET_SELECTOR}`);
      });
    }
  }, [hasToaster]);
  const offsetCalendar = appContext.isSeller ? 0 : offsetSell;
  const from = departureDate && !blockInputDepartureDate ? moment(departureDate).toDate() : null;
  const to =
    from && inputDuration
      ? moment(from)
          .add({
            days: inputDuration,
          })
          .toDate()
      : null;
  const [hovered, setHovered] = useState<Date | null | undefined>(from);
  useEffect(() => {
    if (!departureDate) {
      setHovered(null);
    }
  }, [departureDate]);

  const onSelectMonth = (monthId) => {
    if (monthId !== selectedMonth) {
      if (!from && departureDate) {
        setUrlQueries({
          [QUERY_DEPARTURE_DATE]: undefined,
          [QUERY_DURATION]: undefined,
        });
        dispatch(setDepartureDate(null));
        dispatch(clearTripDuration());
      }

      setCalendarToDate(moment(selectedMonth, YEAR_MONTH));
      requestAnimationFrame(() => {
        setCalendarToDate(moment(monthId, YEAR_MONTH));
        dispatch(selectMonth(monthId));
      });
    }
  };

  const onReset = () => {
    requestAnimationFrame(() => {
      scrollToElement(
        `[data-scroll-anchor="${SCROLL_ANCHOR}"]`,
        500,
        `.${NAVIGATION_OFFSET_SELECTOR}`,
      );
    });
    setUrlQueries({
      [QUERY_DEPARTURE_DATE]: undefined,
      [QUERY_DURATION]: undefined,
    });
    dispatch(setDepartureDate(null));
    dispatch(clearTripDuration());
    setInputDuration(null);
    setHasToaster(false);
  };

  const onDayMouseOver = (day: Date | null | undefined) => {
    setHovered(day);
  };

  const displayedDuration =
    from && (to || hovered)
      ? dateDiff(from, to || hovered || from) || durationPrice
      : durationPrice;
  const durationLabel = messageFormat({
    n: t('faceting.when.durationPlural'),
    _0: '-',
    _1: t('faceting.when.durationSingular'),
  }).format({
    n: displayedDuration,
    count: displayedDuration,
  });
  const { locale } = appContext;
  const selectedDayPrice = selectedDay && prices && prices[selectedDay];
  const instalmentSelectedDayPrice = getInstallmentPrice(
    {
      currency: selectedDayPrice?.currency,
      formattedValue: selectedDayPrice?.price,
      value: selectedDayPrice?.priceValue,
    },
    locale,
  );
  const selectedBestPrice =
    inputDuration && inputDuration !== durationPrice
      ? getInstallmentPrice(price?.bestPrice, locale)
      : instalmentSelectedDayPrice;
  const shouldRenderRealPrice = Boolean(
    !isLoading && !isPriceOfStayLoading && priceOfStay && hasValidCriteria,
  );
  const selectedRealPrice = shouldRenderRealPrice
    ? getInstallmentPrice(priceOfStay.bestPrice, locale)
    : '';
  const selectedDuration = inputDuration ? inputDuration : 0;
  const dateFormat = t('booking.form.dateFormat');
  const longDateFormat = t('resort.startingPrice.longDateFormat');
  return (
    <div
      className="relative isolate -mx-20 min-h-450 md:mx-0"
      data-scroll-anchor={SCROLL_ANCHOR}
      id="calendar"
    >
      {!isEmpty(calendar) && (
        <Fragment>
          <BookingCalendarMonths
            isLoading={isLoading}
            months={calendar.blocks.map((b) => ({
              id: b.id,
              isActive: b.id === selectedMonth,
              name: b.name,
              price: getInstallmentPrice(b.price, locale),
              shortName: b.shortName,
              year: b.year,
            }))}
            onClick={onSelectMonth}
          />
          <PriceLegend durationLabel={durationLabel} canReset={!!from} onReset={onReset} />
          <TripDatePicker
            className="-mx-1 md:mx-0 md:mb-40"
            displayPrices
            duration={durationPrice}
            hasInstalment
            isJoyview={isJoyview}
            navbarElement={({ month }) => {
              const datePickerMonth = Number(moment(month).format(YEAR_MONTH));

              if (datePickerMonth !== selectedMonth && !isLoading && (from || to)) {
                dispatch(selectMonth(datePickerMonth));
              }

              return null;
            }}
            onSelect={onSelect}
            onDayMouseOver={onDayMouseOver}
            optionsDuration={availableDurations?.length ? availableDurations : []}
            openCalendarToDate={openCalendarToDate}
            prices={prices}
            product={product}
            offsetCalendar={offsetCalendar}
            selectedBestPrice={selectedBestPrice}
            selectedRealPrice={selectedRealPrice}
            selectedDays={{
              from,
              to,
            }}
            selectedDuration={selectedDuration}
            selectedMonth={selectedMonth}
            withLongDayFormat={isTablet}
            withPriceDetails={isDesktop}
          />
          <ArrivalDepartureDate
            arrivalDate={from && moment(from).format(dateFormat)}
            departureDate={to && moment(to).format(dateFormat)}
          />
          {from && to && hasToaster && !isLoading && (
            <BookingCalendarResume
              className="absolute bottom-0 end-0 z-3 mx-20 mt-[-100px] w-[calc(100%-2.5rem)] md:bottom-auto md:mx-0 md:mt-[-350px] md:w-380 ltr:animate-slideInRight rtl:animate-slideInLeft"
              hasTransport={Boolean(transport && transport !== NO_TRANSPORT_VALUE)}
              id={HASH}
              isJoyview={isJoyview}
              endDate={moment(to).format(longDateFormat)}
              onClose={() => setHasToaster(false)}
              onReset={onReset}
              price={selectedBestPrice}
              priceIsLoading={priceIsLoading}
              rate={selectedDayPrice?.rate}
              realPrice={selectedRealPrice}
              realPriceIsLoading={isPriceOfStayLoading}
              startDate={moment(from).format(longDateFormat)}
            />
          )}
        </Fragment>
      )}
      {isLoading && (
        <div className="absolute inset-0 z-1 flex w-full items-center justify-center bg-lightSand">
          <AsyncLoader />
        </div>
      )}
    </div>
  );
};
